/* eslint-disable @typescript-eslint/no-unused-vars */
// filepath: /Users/keerthivelagala/Desktop/AzureRepo/pbna-wse-lighthouse-frontend/src/screens/NewHireOnboarding/components/EmployeeProfile/EmployeeMetricTabs.tsx
import React, { useState, useEffect } from "react";
import {
  View,
  StyleSheet,
  Text,
  TouchableOpacity,
  Platform,
  Dimensions,
} from "react-native";
import { createApiClient, apiClient } from "../../../../api/apiClient";
import { useSelector } from "react-redux";
import { handleNHOErrorWithFriendlyMessage, NHO_API_NAMES } from '../../../../store/utils/nhoErrorHandler';
import { convertProbationToMilestone } from '../../../../constants';
import { useTheme } from "../../../../theme";

// Cross-platform import for Victory components
let VictoryChart: any,
  VictoryLine: any,
  VictoryAxis: any,
  VictoryTheme: any,
  VictoryBar: any,
  VictoryPie: any,
  VictoryContainer: any,
  VictoryGroup: any;

if (Platform.OS === "web") {
  // @ts-ignore
  ({
    VictoryChart,
    VictoryLine,
    VictoryAxis,
    VictoryTheme,
    VictoryBar,
    VictoryPie,
    VictoryContainer,
    VictoryGroup,
  } = require("victory"));
} else {
  // @ts-ignore
  ({
    VictoryChart,
    VictoryLine,
    VictoryAxis,
    VictoryTheme,
    VictoryBar,
    VictoryPie,
    VictoryContainer,
    VictoryGroup,
  } = require("victory-native"));
}

// Define tab type
type Tab = {
  id: string;
  title: string;
  icon: string;
};

interface EmployeeTabsProps {
  tabs: Tab[];
  activeTab: string;
  setActiveTab: (tabId: string) => void;
  employeeId?: string; // Optional employee ID for filtering metrics
}

// Define probation summary data interface
interface ProbationSummary {
  "Preferred Method": string | null;
  "Probation 30": any | null;
  "Probation 60": any | null;
  "Probation 90": any | null;
  "Pulse Check": {
    user: {
      First_Name: string;
      Last_Name: string;
      Peer_Coach: string | null;
      Expected_Graduation_Date: string | null;
      Weeks_till_graduation: number | null;
    };
    metrics: any[];
    attendance: any[];
    observations: string;
    additional_observations: string;
    employee_signature: string;
    manager_signature: string;
  };
}

const EmployeeMetricTabs: React.FC<EmployeeTabsProps> = ({
  tabs,
  activeTab,
  setActiveTab,
  employeeId,
}) => {
  const { colors, resolvedTheme } = useTheme();
  const isDarkTheme = resolvedTheme === "dark";
  const windowWidth = Dimensions.get("window").width;
  const isIphone = Platform.OS === "ios" && windowWidth < 768;
  const isIpad = Platform.OS === "ios" && windowWidth >= 768;
  const isWeb = Platform.OS === "web";
  const tabBarWidth = isIphone ? "90%" : isIpad ? "50%" : isWeb ? "30%" : "30%";
  // State for API data
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [metricsData, setMetricsData] = useState<any[]>([]);
  const [probationSummary, setProbationSummary] =
    useState<ProbationSummary | null>(null);

  // Get selected location from Redux store
  const { selectedLocation } = useSelector((state: any) => state.appData);

  // Transform API data for charts
  const getDpsData = () => {
    const actual = metricsData.map((item) => ({
      x: item.Week,
      y: item.DPS_Actual || 0,
    }));

    const target = metricsData.map((item) => ({
      x: item.Week,
      y: item.DPS_Target || 0,
    }));

    return { actual, target };
  };

  const getCphData = () => {
    const actual = metricsData.map((item) => ({
      x: item.Week,
      y: item.CPH_Actual || 0,
    }));

    const target = metricsData.map((item) => ({
      x: item.Week,
      y: item.CPH_Target || 0,
    }));

    return { actual, target };
  };

  const getGapTimeData = () => {
    const actual = metricsData.map((item) => ({
      x: item.Week,
      y: item.Gap_Time_Actual || 0,
    }));

    const target = metricsData.map((item) => ({
      x: item.Week,
      y: item.GAP_Time_Target || 0,
    }));

    return { actual, target };
  };

  const getSiaData = () => {
    const actual = metricsData.map((item) => ({
      x: item.Week,
      y: item.SIA_Actual || 0,
    }));

    const target = metricsData.map((item) => ({
      x: item.Week,
      y: item.SIA_Target || 0,
    }));

    return { actual, target };
  };

  // Get attendance data for the table
  const getAttendanceData = () => {
    if (
      probationSummary &&
      probationSummary["Pulse Check"]?.attendance &&
      Array.isArray(probationSummary["Pulse Check"].attendance)
    ) {
      return probationSummary["Pulse Check"].attendance.map((item, index) => ({
        serialNumber: index + 1,
        attendance: new Date(item.Date_of_Absence).toLocaleDateString(),
        points: item.Points || 0,
        // absences: item.Absences || 0 // Removed
      }));
    }
    // Fallback to metrics data if no attendance in probation summary
    return metricsData.map((item, index) => ({
      serialNumber: index + 1,
      attendance: `Week ${index + 1}`,
      points: item.Attendance || 0,
      // absences: 0 // Removed
    }));
  };

  // Fetch probation summary data using employee ID
  const fetchProbationSummary = async () => {
    if (!employeeId) {
      console.log("No employee ID available for probation summary");
      return;
    }

    try {
      // Get location name from Redux state
      const location =
        selectedLocation?.displayText || selectedLocation?.name || "";
      if (!location) {
        console.log("No location selected for probation summary");
        setError("No location selected. Please select a location first.");
        return;
      }

      setLoading(true);

      // Create API client instance
      const api = createApiClient();

      // Build URL for the probation summary endpoint - ensure full path is included
      const url = `/new-hire-onboarding/probation/summary/${employeeId}?location=${encodeURIComponent(
        location
      )}`;

      console.log("Making probation summary API request to:", url);

      // Make API call
      const response = await apiClient(api, url, "GET");
      console.log("Probation summary API response:", response);

      if (response) {
        setProbationSummary(response);

        // If we have Pulse Check metrics data, use it to update our metrics data
        if (
          response["Pulse Check"]?.metrics &&
          Array.isArray(response["Pulse Check"].metrics)
        ) {
          console.log(
            "Using metrics data from Pulse Check:",
            response["Pulse Check"].metrics
          );
          setMetricsData(response["Pulse Check"].metrics);
        }
      } else {
        console.log("No probation summary data received");
      }
    } catch (err) {
      console.error("Error fetching probation summary data:", err);
      try {
        handleNHOErrorWithFriendlyMessage(NHO_API_NAMES.FETCH_PROBATION_SUMMARY, err, true, { employeeId, location: selectedLocation?.displayText });
      } catch (e) {
        console.error('Error while dispatching NHO centralized handler:', e);
      }
      setError("Failed to fetch probation summary data");
    } finally {
      setLoading(false);
    }
  };

  // Load data on component mount
  useEffect(() => {
    // fetchMetricsData();
    fetchProbationSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocation, employeeId]);

  // Get chart data based on current state
  const { actual: dpsActual, target: dpsGoal } = getDpsData();
  const { actual: cphActual, target: cphGoal } = getCphData();
  const { actual: gapTimeActual, target: gapTimeGoal } = getGapTimeData();
  const { actual: siaActual, target: siaGoal } = getSiaData();

  // Compute SIA data for pie chart
  const computeSiaChartData = () => {
    // Calculate average compliance rate for simplicity
    const totalSiaActual = metricsData.reduce(
      (sum, item) => sum + (item.SIA_Actual || 0),
      0
    );
    const avgSiaActual =
      metricsData.length > 0 ? totalSiaActual / metricsData.length : 0;

    // For the pie chart: compliance, minor issues, major issues
    return [
      { x: "No Incident", y: avgSiaActual },
      { x: "Minor", y: Math.max(0, 100 - avgSiaActual) * 0.7 }, // 70% of remaining as minor
      { x: "Major", y: Math.max(0, 100 - avgSiaActual) * 0.3 }, // 30% of remaining as major
    ];
  };

  // Get user information from probation summary
  const getUserInfo = () => {
    if (!probationSummary || !probationSummary["Pulse Check"]?.user) {
      return null;
    }

    const user = probationSummary["Pulse Check"].user;
    return {
      fullName: `${user.First_Name || ""} ${user.Last_Name || ""}`.trim(),
      peerCoach: user.Peer_Coach || "Not Assigned",
      expectedGraduationDate: user.Expected_Graduation_Date
        ? new Date(user.Expected_Graduation_Date).toLocaleDateString()
        : "Not Set",
      weeksTillGraduation:
        user.Weeks_till_graduation !== null
          ? `${user.Weeks_till_graduation} weeks`
          : "Not Available",
    };
  };

  const siaData = computeSiaChartData();
  const attendanceData = getAttendanceData();
  const userInfo = getUserInfo();

  // Colors for the SIA pie chart
  const siaColorScale = ["#4CAF50", "#FFC107", "#FF5722"];

  return (
    <View
      style={[
        styles.container,
        isDarkTheme && { backgroundColor: colors.background.surface },
      ]}
    >
      {/* Tab Navigation */}
      <View
        style={[
          styles.tabBar,
          { width: tabBarWidth },
          isDarkTheme && {
            backgroundColor: colors.background.surface,
            borderBottomColor: colors.border.default,
          },
        ]}
      >
        {tabs.map((tab) => (
          <TouchableOpacity
            key={tab.id}
            style={[
              styles.tab,
              activeTab === tab.id && styles.activeTab,
              isDarkTheme &&
                activeTab === tab.id && { borderBottomColor: colors.brand.primary },
            ]}
            onPress={() => setActiveTab(tab.id)}
          >
            <Text
              style={[
                styles.tabText,
                activeTab === tab.id && styles.activeTabText,
                isDarkTheme &&
                  (activeTab === tab.id
                    ? { color: colors.brand.primary }
                    : { color: colors.text.primary }),
              ]}
            >
              {tab.title}
            </Text>
          </TouchableOpacity>
        ))}
      </View>

      {/* Tab Content */}
      <View
        style={[
          styles.tabContent,
          isDarkTheme && { backgroundColor: colors.background.surface },
        ]}
      >
        {/* Loading State */}
        {loading && (
          <View style={styles.tabContentPlaceholder}>
            <Text
              style={[
                styles.placeholderText,
                isDarkTheme && { color: colors.text.primary },
              ]}
            >
              Loading metrics data...
            </Text>
          </View>
        )}

        {/* Error State */}
        {error && !loading && (
          <View style={styles.tabContentPlaceholder}>
            <Text
              style={[
                styles.placeholderText,
                isDarkTheme && { color: colors.text.primary },
              ]}
            >
              Error: {error}
            </Text>
          </View>
        )}

        {/* No Data State - Show only for metric tabs (not probation) when no data */}
        {!loading && !error && metricsData.length === 0 && activeTab !== "probation" && (
          <View style={styles.tabContentPlaceholder}>
            <Text
              style={[
                styles.placeholderText,
                isDarkTheme && { color: colors.text.primary },
              ]}
            >
              No metrics data available
            </Text>
          </View>
        )}


        {/* Probation Summary Tab */}
        {!loading &&
          !error &&
          probationSummary &&
          activeTab === "probation" && (
            <View
              style={[
                styles.chartContainer,
                isDarkTheme && { backgroundColor: colors.background.surface },
              ]}
            >
              <Text
                style={[styles.chartTitle, isDarkTheme && { color: colors.text.primary }]}
              >
                Probation Summary
              </Text>

              <View
                style={[
                  styles.probationContainer,
                  isDarkTheme && {
                    backgroundColor: colors.background.elevated,
                    borderColor: colors.border.default,
                  },
                ]}
              >
                <Text
                  style={[
                    styles.probationSectionTitle,
                    isDarkTheme && { color: colors.text.primary },
                  ]}
                >
                  Preferred Method
                </Text>
                <Text
                  style={[
                    styles.observationsText,
                    isDarkTheme && { color: colors.text.primary },
                  ]}
                >
                  {probationSummary["Preferred Method"] || "Not specified"}
                </Text>

                <Text
                  style={[
                    styles.probationSectionTitle,
                    isDarkTheme && { color: colors.text.primary },
                  ]}
                >
                  {convertProbationToMilestone("Probation 30")}
                </Text>
                <Text
                  style={[
                    styles.observationsText,
                    isDarkTheme && { color: colors.text.primary },
                  ]}
                >
                  {probationSummary["Probation 30"]
                    ? JSON.stringify(probationSummary["Probation 30"])
                    : "Not available"}
                </Text>

                <Text
                  style={[
                    styles.probationSectionTitle,
                    isDarkTheme && { color: colors.text.primary },
                  ]}
                >
                  {convertProbationToMilestone("Probation 60")}
                </Text>
                <Text
                  style={[
                    styles.observationsText,
                    isDarkTheme && { color: colors.text.primary },
                  ]}
                >
                  {probationSummary["Probation 60"]
                    ? JSON.stringify(probationSummary["Probation 60"])
                    : "Not available"}
                </Text>

                <Text
                  style={[
                    styles.probationSectionTitle,
                    isDarkTheme && { color: colors.text.primary },
                  ]}
                >
                  {convertProbationToMilestone("Probation 90")}
                </Text>
                <Text
                  style={[
                    styles.observationsText,
                    isDarkTheme && { color: colors.text.primary },
                  ]}
                >
                  {probationSummary["Probation 90"]
                    ? JSON.stringify(probationSummary["Probation 90"])
                    : "Not available"}
                </Text>

                {probationSummary["Pulse Check"] &&
                  probationSummary["Pulse Check"].observations && (
                    <>
                      <Text
                        style={[
                          styles.probationSectionTitle,
                          isDarkTheme && { color: colors.text.primary },
                        ]}
                      >
                        Pulse Check Observations
                      </Text>
                      <Text
                        style={[
                          styles.observationsText,
                          isDarkTheme && { color: colors.text.primary },
                        ]}
                      >
                        {probationSummary["Pulse Check"].observations || "None"}
                      </Text>
                    </>
                  )}

                {probationSummary["Pulse Check"] &&
                  probationSummary["Pulse Check"].additional_observations && (
                    <>
                      <Text
                        style={[
                          styles.probationSectionTitle,
                          isDarkTheme && { color: colors.text.primary },
                        ]}
                      >
                        Additional Observations
                      </Text>
                      <Text
                        style={[
                          styles.observationsText,
                          isDarkTheme && { color: colors.text.primary },
                        ]}
                      >
                        {probationSummary["Pulse Check"]
                          .additional_observations || "None"}
                      </Text>
                    </>
                  )}

                {probationSummary["Pulse Check"] &&
                  probationSummary["Pulse Check"].attendance &&
                  probationSummary["Pulse Check"].attendance.length > 0 && (
                    <>
                      <Text
                        style={[
                          styles.probationSectionTitle,
                          isDarkTheme && { color: colors.text.primary },
                        ]}
                      >
                        Attendance Records
                      </Text>
                      <View style={styles.attendanceRecords}>
                        {probationSummary["Pulse Check"].attendance.map(
                          (record, index) => (
                            <View
                              key={index}
                              style={[
                                styles.attendanceRecord,
                                isDarkTheme && { borderBottomColor: colors.border.default },
                              ]}
                            >
                              <Text
                                style={[
                                  styles.attendanceDate,
                                  isDarkTheme && { color: colors.text.primary },
                                ]}
                              >
                                {new Date(
                                  record.Date_of_Absence
                                ).toLocaleDateString()}
                              </Text>
                              <Text
                                style={[
                                  styles.attendancePoints,
                                  isDarkTheme && { color: colors.text.primary },
                                ]}
                              >
                                {record.Points} points
                              </Text>
                            </View>
                          )
                        )}
                      </View>
                    </>
                  )}
              </View>
            </View>
          )}

        {/* DPS Tab - Combined Chart */}
        {!loading &&
          !error &&
          metricsData.length > 0 &&
          activeTab === "dps" && (
            <View style={styles.chartContainer}>
              <Text
                style={[styles.chartTitle, isDarkTheme && { color: colors.text.primary }]}
              >
                DPS - Actual vs Goal
              </Text>
              <VictoryChart
                theme={VictoryTheme.material}
                height={280}
                width={700}
                padding={{ top: 20, bottom: 80, left: 60, right: 40 }}
                domainPadding={{ x: 0, y: [10, 10] }}
              >
                <VictoryAxis
                  tickFormat={(t: string | number) => t}
                  style={{
                    tickLabels: {
                      fontSize: 9,
                      padding: 5,
                      angle: -45,
                      textAnchor: "end",
                      fill: "#FFFFFF",
                    },
                    axis: { stroke: "#ccc" },
                    grid: { stroke: "#e9ecef", strokeDasharray: "3,3" },
                  }}
                />
                <VictoryAxis
                  dependentAxis
                  tickFormat={(t: string | number) => `${t}`}
                  style={{
                    tickLabels: { fontSize: 10, padding: 5 },
                    axis: { stroke: "#ccc" },
                    grid: { stroke: "#e9ecef", strokeDasharray: "3,3" },
                  }}
                />
                <VictoryLine
                  name="actual"
                  interpolation="basis"
                  style={{
                    data: { stroke: "#33ABDA", strokeWidth: 3 },
                    parent: { border: "1px solid #ccc" },
                  }}
                  data={dpsActual}
                  animate={{
                    duration: 2000,
                    onLoad: { duration: 1000 },
                  }}
                />
                <VictoryLine
                  name="goal"
                  interpolation="basis"
                  style={{
                    data: {
                      stroke: "#B05788",
                      strokeWidth: 3,
                    },
                    parent: { border: "1px solid #ccc" },
                  }}
                  data={dpsGoal}
                  animate={{
                    duration: 2000,
                    onLoad: { duration: 1000 },
                  }}
                />
              </VictoryChart>
              <View style={styles.legendContainer}>
                <View style={styles.legendItem}>
                  <View
                    style={[styles.legendColor, { backgroundColor: "#33ABDA" }]}
                  />
                  <Text
                    style={[styles.legendText, isDarkTheme && { color: colors.text.primary }]}
                  >
                    Actual
                  </Text>
                </View>
                <View style={styles.legendItem}>
                  <View
                    style={[styles.legendColor, { backgroundColor: "#B05788" }]}
                  />
                  <Text
                    style={[styles.legendText, isDarkTheme && { color: colors.text.primary }]}
                  >
                    Target
                  </Text>
                </View>
              </View>
            </View>
          )}

        {/* CPH Tab - Combined Chart */}
        {!loading &&
          !error &&
          metricsData.length > 0 &&
          activeTab === "cph" && (
            <View style={styles.chartContainer}>
              <Text
                style={[styles.chartTitle, isDarkTheme && { color: colors.text.primary }]}
              >
                CPH - Actual vs Goal
              </Text>
              <VictoryChart
                theme={VictoryTheme.material}
                height={280}
                width={700}
                padding={{ top: 20, bottom: 80, left: 60, right: 40 }}
                domainPadding={{ x: 0, y: [10, 10] }}
              >
                <VictoryAxis
                  tickFormat={(t: string | number) => t}
                  style={{
                    tickLabels: {
                      fontSize: 9,
                      padding: 5,
                      angle: -45,
                      textAnchor: "end",
                    },
                    axis: { stroke: "#ccc" },
                    grid: { stroke: "#e9ecef", strokeDasharray: "3,3" },
                  }}
                />
                <VictoryAxis
                  dependentAxis
                  tickFormat={(t: string | number) => `${t}`}
                  style={{
                    tickLabels: { fontSize: 10, padding: 5 },
                    axis: { stroke: "#ccc" },
                    grid: { stroke: "#e9ecef", strokeDasharray: "3,3" },
                  }}
                />
                <VictoryLine
                  name="actual"
                  interpolation="basis"
                  style={{
                    data: { stroke: "#33ABDA", strokeWidth: 3 },
                    parent: { border: "1px solid #ccc" },
                  }}
                  data={cphActual}
                  animate={{
                    duration: 2000,
                    onLoad: { duration: 1000 },
                  }}
                />
                <VictoryLine
                  name="goal"
                  interpolation="basis"
                  style={{
                    data: {
                      stroke: "#B05788",
                      strokeWidth: 3,
                    },
                    parent: { border: "1px solid #ccc" },
                  }}
                  data={cphGoal}
                  animate={{
                    duration: 2000,
                    onLoad: { duration: 1000 },
                  }}
                />
              </VictoryChart>
              <View style={styles.legendContainer}>
                <View style={styles.legendItem}>
                  <View
                    style={[styles.legendColor, { backgroundColor: "#33ABDA" }]}
                  />
                  <Text
                    style={[styles.legendText, isDarkTheme && { color: colors.text.primary }]}
                  >
                    Actual
                  </Text>
                </View>
                <View style={styles.legendItem}>
                  <View
                    style={[styles.legendColor, { backgroundColor: "#B05788" }]}
                  />
                  <Text
                    style={[styles.legendText, isDarkTheme && { color: colors.text.primary }]}
                  >
                    Target
                  </Text>
                </View>
              </View>
            </View>
          )}

        {/* Gap Time Tab - Combined Chart */}
        {!loading &&
          !error &&
          metricsData.length > 0 &&
          activeTab === "gap-time" && (
            <View style={styles.chartContainer}>
              <Text
                style={[styles.chartTitle, isDarkTheme && { color: colors.text.primary }]}
              >
                Gap Time - Actual vs Goal
              </Text>
              <VictoryChart
                theme={VictoryTheme.material}
                height={280}
                width={700}
                padding={{ top: 20, bottom: 80, left: 60, right: 40 }}
                domainPadding={{ x: 0, y: [10, 10] }}
              >
                <VictoryAxis
                  tickFormat={(t: string | number) => t}
                  style={{
                    tickLabels: {
                      fontSize: 9,
                      padding: 5,
                      angle: -45,
                      textAnchor: "end",
                    },
                    axis: { stroke: "#ccc" },
                    grid: { stroke: "#e9ecef", strokeDasharray: "3,3" },
                  }}
                />
                <VictoryAxis
                  dependentAxis
                  tickFormat={(t: string | number) => `${t}`}
                  style={{
                    tickLabels: { fontSize: 10, padding: 5 },
                    axis: { stroke: "#ccc" },
                    grid: { stroke: "#e9ecef", strokeDasharray: "3,3" },
                  }}
                />
                <VictoryLine
                  interpolation="basis"
                  style={{
                    data: { stroke: "#33ABDA", strokeWidth: 3 },
                    parent: { border: "1px solid #ccc" },
                  }}
                  data={gapTimeActual}
                  animate={{
                    duration: 2000,
                    onLoad: { duration: 1000 },
                  }}
                />
                <VictoryLine
                  interpolation="basis"
                  style={{
                    data: {
                      stroke: "#B05788",
                      strokeWidth: 3,
                    },
                    parent: { border: "1px solid #ccc" },
                  }}
                  data={gapTimeGoal}
                  animate={{
                    duration: 2000,
                    onLoad: { duration: 1000 },
                  }}
                />
              </VictoryChart>
              <View style={styles.legendContainer}>
                <View style={styles.legendItem}>
                  <View
                    style={[styles.legendColor, { backgroundColor: "#33ABDA" }]}
                  />
                  <Text
                    style={[styles.legendText, isDarkTheme && { color: colors.text.primary }]}
                  >
                    Actual
                  </Text>
                </View>
                <View style={styles.legendItem}>
                  <View
                    style={[styles.legendColor, { backgroundColor: "#B05788" }]}
                  />
                  <Text
                    style={[styles.legendText, isDarkTheme && { color: colors.text.primary }]}
                  >
                    Target
                  </Text>
                </View>
              </View>
            </View>
          )}

        {/* SIA Tab - Combined Chart */}
        {!loading &&
          !error &&
          metricsData.length > 0 &&
          activeTab === "sia" && (
            <View style={styles.chartContainer}>
              <Text
                style={[styles.chartTitle, isDarkTheme && { color: colors.text.primary }]}
              >
                SIA - Actual vs Goal
              </Text>
              <VictoryChart
                theme={VictoryTheme.material}
                height={280}
                width={700}
                padding={{ top: 20, bottom: 80, left: 60, right: 40 }}
                domainPadding={{ x: 0, y: [10, 10] }}
              >
                <VictoryAxis
                  tickFormat={(t: string | number) => t}
                  style={{
                    tickLabels: {
                      fontSize: 9,
                      padding: 5,
                      angle: -45,
                      textAnchor: "end",
                    },
                    axis: { stroke: "#ccc" },
                    grid: { stroke: "#e9ecef", strokeDasharray: "3,3" },
                  }}
                />
                <VictoryAxis
                  dependentAxis
                  tickFormat={(t: string | number) => `${t}`}
                  style={{
                    tickLabels: { fontSize: 10, padding: 5 },
                    axis: { stroke: "#ccc" },
                    grid: { stroke: "#e9ecef", strokeDasharray: "3,3" },
                  }}
                />
                <VictoryLine
                  name="actual"
                  interpolation="basis"
                  style={{
                    data: { stroke: "#33ABDA", strokeWidth: 3 },
                    parent: { border: "1px solid #ccc" },
                  }}
                  data={siaActual}
                  animate={{
                    duration: 2000,
                    onLoad: { duration: 1000 },
                  }}
                />
                <VictoryLine
                  name="goal"
                  interpolation="basis"
                  style={{
                    data: {
                      stroke: "#B05788",
                      strokeWidth: 3,
                    },
                    parent: { border: "1px solid #ccc" },
                  }}
                  data={siaGoal}
                  animate={{
                    duration: 2000,
                    onLoad: { duration: 1000 },
                  }}
                />
              </VictoryChart>
              <View style={styles.legendContainer}>
                <View style={styles.legendItem}>
                  <View
                    style={[styles.legendColor, { backgroundColor: "#33ABDA" }]}
                  />
                  <Text
                    style={[styles.legendText, isDarkTheme && { color: colors.text.primary }]}
                  >
                    Actual
                  </Text>
                </View>
                <View style={styles.legendItem}>
                  <View
                    style={[styles.legendColor, { backgroundColor: "#B05788" }]}
                  />
                  <Text
                    style={[styles.legendText, isDarkTheme && { color: colors.text.primary }]}
                  >
                    Target
                  </Text>
                </View>
              </View>
            </View>
          )}

        {/* Attendance Tab - Bar Chart */}
        {!loading && !error && metricsData.length > 0 && activeTab === "attendance" && (
          <View style={styles.chartContainer}>
            <Text
              style={[styles.chartTitle, isDarkTheme && { color: colors.text.primary }]}
            >
              Attendance Records
            </Text>
            <VictoryChart
              theme={VictoryTheme.material}
              height={280}
              width={700}
              padding={{ top: 20, bottom: 80, left: 60, right: 40 }}
              domainPadding={{ x: 20, y: [10, 10] }}
            >
              <VictoryAxis
                tickFormat={(t: string | number) => {
                  // Format week ticks as integers (e.g. W1, W2, W3).
                  // If tick is numeric or a numeric string, coerce and round to nearest integer.
                  const n = Number(t as any);
                  if (!Number.isNaN(n)) return `W${Math.round(n)}`;
                  // Fallback: show raw value prefixed with W
                  return `W${t}`;
                }}
                style={{
                  tickLabels: {
                    fontSize: 9,
                    padding: 5,
                    angle: -45,
                    textAnchor: "end",
                  },
                  axis: { stroke: "#ccc" },
                  grid: { stroke: "#e9ecef", strokeDasharray: "3,3" },
                }}
              />
              <VictoryAxis
                dependentAxis
                tickFormat={(t: string | number) => `${t}`}
                label="Points"
                style={{
                  tickLabels: { fontSize: 10, padding: 5 },
                  axisLabel: { fontSize: 12, padding: 35 },
                  axis: { stroke: "#ccc" },
                  grid: { stroke: "#e9ecef", strokeDasharray: "3,3" },
                }}
              />
              <VictoryBar
                alignment="start"
                data={attendanceData.map((item, index) => ({
                  x: index + 1,
                  y: item.points,
                  label: `${item.points} pts`,
                }))}
                style={{
                  data: {
                    fill: "#33ABDA",
                    width: 30,
                  },
                }}
              />
            </VictoryChart>
            <View style={styles.legendContainer}>
              <View style={styles.legendItem}>
                <View
                  style={[styles.legendColor, { backgroundColor: "#33ABDA" }]}
                />
                <Text
                  style={[styles.legendText, isDarkTheme && { color: colors.text.primary }]}
                >
                  Attendance Points
                </Text>
              </View>
            </View>
          </View>
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    //marginHorizontal: 20, // Reduced horizontal padding to allow more space
    paddingBottom: 20,
    backgroundColor: "white",
  },
  tabBar: {
    flexDirection: "row",
    backgroundColor: "#fff",
    borderBottomWidth: 1,
    borderBottomColor: "#e9ecef",
    width: Platform.OS === "ios" && Dimensions.get("window").width < 768 ? "90%" : "30%",
    marginLeft:20,
    marginRight:20,
  },
  tab: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 12,
    paddingHorizontal: 8,
    borderBottomWidth: 2,
    borderBottomColor: "transparent",
  },
  activeTab: {
    borderBottomColor: "#0066cc",
  },
  tabIcon: {
    marginRight: 4,
    color: "#495057",
  },
  activeTabIcon: {
    color: "#0066cc",
  },
  tabText: {
    fontSize: Platform.OS === "web" ? 14 : 12,
    fontWeight: "500",
    color: "#495057",
  },
  activeTabText: {
    color: "#0066cc",
    fontWeight: "500",
  },
  tabContent: {
    height: Platform.OS === "web" ? 500 : 400, // Slightly increased height
    backgroundColor: "#fff",
    position: "relative",
    overflow: "hidden", // Changed to hidden to prevent content from spilling out
    alignItems: "center",
    width: "100%", // Ensure full width
  },
  tabContentPlaceholder: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 20,
  },
  placeholderText: {
    fontSize: 16,
    color: "#6c757d",
    textAlign: "center",
  },
  chartContainer: {
    flex: 1,
    padding: 10,
    //backgroundColor: "#fff",
    paddingBottom: 60, // Add more padding at the bottom for the legend
    position: "relative", // Position context for absolute children
    alignItems: "center", // Center the chart horizontally
    width: "80%",
  },
  tableContainer: {
    flex: 1,
    padding: 10,
    backgroundColor: "#fff",
    paddingBottom: 20,
    position: "relative",
    width: "100%", // Full width for tables
    alignSelf: "center",
  },
  chartWithLegendContainer: {
    flexDirection: "column", // Changed to column to stack chart and legend
    alignItems: "center",
    position: "relative",
    width: "100%",
  },
  chartContent: {
    flex: 1,
    padding: 10,
  },
  chartTitle: {
    fontSize: 16,
    fontWeight: "600",
    color: "#333",
    textAlign: "center",
    marginBottom: 15,
  },
  pieSubtitle: {
    fontSize: 14,
    fontWeight: "500",
    color: "#333",
    textAlign: "center",
    marginTop: 5,
  },
  pieChartContainer: {
    alignItems: "center",
    justifyContent: "center",
    marginVertical: 10,
  },
  legendContainer: {
    flexDirection: "row",
    justifyContent: "center",
    marginTop: -15,
    // backgroundColor: '#fff',
    paddingVertical: 8,
    paddingHorizontal: 15,
    // borderRadius: 5,
    // borderWidth: 1,
    borderColor: "#e9ecef",
    width: 800, // Match the width of the chart
    zIndex: 10,
    position: "relative",
    alignSelf: "center",
  },
  legendItem: {
    flexDirection: "row",
    alignItems: "center",
    marginVertical: 5,
    marginHorizontal: 15,
    //backgroundColor: "#f8f9fa",
    paddingVertical: 3,
    paddingHorizontal: 5,
    borderRadius: 3,
  },
  legendColor: {
    width: 20,
    height: 20,
    borderRadius: 50,
    marginRight: 8,
    //borderWidth: 1,
    //borderColor: "#ddd",
  },
   legendText: {
     fontSize: 16,
     fontWeight: "700", // Make text bolder
     color: "#FFFFFF", // White color for better visibility
     marginLeft: 5,
   },
  chartRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    width: "100%",
  },
  chartColumn: {
    width: "48%",
  },
  userInfoContainer: {
    backgroundColor: "#f8f9fa",
    borderRadius: 8,
    padding: 15,
    marginBottom: 15,
    borderWidth: 1,
    borderColor: "#e9ecef",
  },
  userInfoTitle: {
    fontSize: 16,
    fontWeight: "600",
    color: "#333",
    marginBottom: 10,
  },
  userInfoContent: {
    flexDirection: "row",
    flexWrap: "wrap",
  },
  userInfoRow: {
    flexDirection: "row",
    width: "50%",
    paddingVertical: 4,
  },
  userInfoLabel: {
    fontSize: 14,
    fontWeight: "500",
    color: "#495057",
    width: 160,
  },
  userInfoValue: {
    fontSize: 14,
    color: "#212529",
    flex: 1,
  },
  probationContainer: {
    backgroundColor: "#fff",
    borderRadius: 8,
    padding: 15,
    marginTop: 15,
    borderWidth: 1,
    borderColor: "#e9ecef",
  },
  probationTitle: {
    fontSize: 18,
    fontWeight: "600",
    color: "#333",
    marginBottom: 15,
    textAlign: "center",
  },
  probationSectionTitle: {
    fontSize: 16,
    fontWeight: "500",
    color: "#495057",
    marginTop: 15,
    marginBottom: 8,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: "500",
    color: "#495057",
    marginTop: 15,
    marginBottom: 8,
  },
  normalText: {
    fontSize: 14,
    color: "#212529",
    lineHeight: 20,
  },
  attendanceRecords: {
    marginVertical: 10,
  },
  attendanceRecord: {
    flexDirection: "row",
    justifyContent: "space-between",
    paddingVertical: 6,
    borderBottomWidth: 1,
    borderBottomColor: "#e9ecef",
  },
  attendanceDate: {
    fontSize: 14,
    color: "#212529",
  },
  attendancePoints: {
    fontSize: 14,
    fontWeight: "500",
    color: "#dc3545",
  },
  observationsText: {
    fontSize: 14,
    color: "#212529",
    lineHeight: 20,
  },
  signaturesContainer: {
    marginTop: 10,
  },
  signatureLabel: {
    fontSize: 14,
    fontWeight: "500",
    color: "#495057",
    marginTop: 8,
  },
  signatureValue: {
    fontSize: 14,
    color: "#212529",
    marginBottom: 8,
  },
});

export default EmployeeMetricTabs;
